import { useCallback, useSyncExternalStore } from 'react';

/**
 * usePinnedItems — single source of truth for which apps/agents are pinned
 * to the project sidebar. FRONTEND-ONLY: there is no backend route; the
 * user's choices live in localStorage, scoped per project.
 *
 * DEFAULT IS PINNED. Every app and agent shows in the sidebar unless the
 * user explicitly turns it OFF. So we only ever PERSIST the exceptions:
 * the store is a map of `{ [itemId]: false }` for items the user unpinned.
 * A missing entry means pinned. (Storing `true` would be redundant — the
 * absence of an `unpin` already means pinned — so an unpin is removed from
 * the map by deleting the key when toggled back on, keeping it minimal.)
 *
 * Reactivity: backed by useSyncExternalStore over a tiny in-module store.
 * Both the kebab menus and the sidebar subscribe, so a toggle in one place
 * updates the other live (same tab). Cross-tab updates come through the
 * native `storage` event. No duplicated localStorage logic across files —
 * everything funnels through here.
 */

const keyFor = (projectId) => `zibby.pinnedItems.${projectId}`;

// Per-project subscriber sets so a toggle only re-renders consumers of the
// affected project's store.
const listeners = new Map(); // projectId -> Set<callback>

const emit = (projectId) => {
  const set = listeners.get(projectId);
  if (set) set.forEach((cb) => cb());
};

const read = (projectId) => {
  if (!projectId) return {};
  try {
    const raw = localStorage.getItem(keyFor(projectId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const write = (projectId, map) => {
  try {
    if (map && Object.keys(map).length > 0) {
      localStorage.setItem(keyFor(projectId), JSON.stringify(map));
    } else {
      // Nothing unpinned → drop the key entirely (keeps storage clean and
      // means "no entry == all-default" stays the canonical empty state).
      localStorage.removeItem(keyFor(projectId));
    }
  } catch {
    /* storage full / disabled — pin state is best-effort, ignore */
  }
};

// Stable empty snapshot for the no-project case (and the SSR fallback) — a
// single shared reference so useSyncExternalStore never sees a "changed" value.
const EMPTY = {};

// useSyncExternalStore needs a STABLE snapshot reference between renders
// when nothing changed, or React warns/loops. Cache the last parsed map per
// project and only swap it when the serialized value actually differs.
const snapshotCache = new Map(); // projectId -> { raw, value }

const getSnapshot = (projectId) => {
  if (!projectId) return EMPTY;
  const raw = (() => {
    try { return localStorage.getItem(keyFor(projectId)) || ''; } catch { return ''; }
  })();
  const cached = snapshotCache.get(projectId);
  if (cached && cached.raw === raw) return cached.value;
  const value = read(projectId);
  snapshotCache.set(projectId, { raw, value });
  return value;
};

export const usePinnedItems = (projectId) => {
  const subscribe = useCallback((cb) => {
    if (!projectId) return () => {};
    let set = listeners.get(projectId);
    if (!set) { set = new Set(); listeners.set(projectId, set); }
    set.add(cb);
    // Cross-tab: another tab wrote our key. Bust the snapshot cache + notify.
    const onStorage = (e) => {
      if (e.key === keyFor(projectId)) {
        snapshotCache.delete(projectId);
        cb();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      set.delete(cb);
      window.removeEventListener('storage', onStorage);
    };
  }, [projectId]);

  const snapshot = useSyncExternalStore(
    subscribe,
    () => getSnapshot(projectId),
    () => EMPTY,
  );

  // Missing entry === pinned (default-on). Only an explicit `false` unpins.
  const isPinned = useCallback(
    (id) => snapshot[id] !== false,
    [snapshot],
  );

  const togglePin = useCallback((id) => {
    if (!projectId || !id) return;
    const current = read(projectId);
    const next = { ...current };
    if (next[id] === false) {
      // Currently unpinned → re-pin = back to default = drop the exception.
      delete next[id];
    } else {
      // Currently pinned (default) → record the explicit unpin.
      next[id] = false;
    }
    write(projectId, next);
    snapshotCache.delete(projectId);
    emit(projectId);
  }, [projectId]);

  return { isPinned, togglePin };
};

export default usePinnedItems;
